// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useActiveProfile, getActiveProfileId } from '@/hooks/useActiveProfile';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useParentEditPermissions } from '@/hooks/useParentEditPermissions';
import { useProfileEditPermissions } from '@/hooks/useProfileEditPermissions';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  type ProfileData, 
  type Academics, 
  type SocialMedia, 
  type PressEntry,
  type Experience,
  type Event,
  type PreferenceData,
  type ProfileExperience,
  type ProfileOffer,
  type AcademicAchievement
} from '@/types/profileTypes';

export function useProfileData() {
  const { user, profile } = useActiveProfile();
  
  // Try to get athlete context, but don't fail if not available
  let athleteProfileId: string | null = null;
  try {
    const { useAthleteView } = require('@/contexts/AthleteViewContext');
    const athleteContext = useAthleteView();
    athleteProfileId = athleteContext?.athleteProfileId || null;
  } catch (error) {
    // AthleteViewProvider not available (e.g., in college coach dashboard)
    console.log('🔍 useProfileData: AthleteView context not available, using direct profile');
    athleteProfileId = null;
  }
  const { isImpersonating, athleteId } = useImpersonation();
  const { canEdit: allowParentEdits } = useParentEditPermissions();
  const location = useLocation();
  
  // 🔍 Enhanced URL parameter detection with comprehensive logging
  const searchParams = new URLSearchParams(location.search);
  const isScoutView = searchParams.get('asScout') === '1';
  const impersonatedProfileId = searchParams.get('profile_id');
  
  console.log('🔍 useProfileData: URL Analysis');
  console.log('🔍 - Current URL:', window.location.href);
  console.log('🔍 - location.search:', location.search);
  console.log('🔍 - searchParams object:', Object.fromEntries(searchParams.entries()));
  console.log('🔍 - isScoutView:', isScoutView);
  console.log('🔍 - impersonatedProfileId:', impersonatedProfileId);
  console.log('🔍 - athleteProfileId from context:', athleteProfileId);
  console.log('🔍 - profile?.id:', profile?.id);
  console.log('🔍 - user?.id:', user?.id);
  
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [academicData, setAcademicData] = useState<Academics | null>(null);
  const [socialMedia, setSocialMedia] = useState<SocialMedia[]>([]);
  const [press, setPress] = useState<PressEntry[]>([]);
  const [experiences, setExperiences] = useState<ProfileExperience[] | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [preferences, setPreferences] = useState<PreferenceData | null>(null);
  const [offers, setOffers] = useState<ProfileOffer[]>([]);
  const [academicAchievements, setAcademicAchievements] = useState<AcademicAchievement[]>([]);
  const [athleticAchievements, setAthleticAchievements] = useState<any[]>([]);
  const [sportStats, setSportStats] = useState<any[]>([]);
  const [combineScores, setCombineScores] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [mediaItems, setMediaItems] = useState<any[]>([]);
  const [profileCompletionPct, setProfileCompletionPct] = useState<number>(0);

  // Get the active profile ID using centralized logic
  const getActiveProfileIdInternal = useCallback(() => {
    console.log('🔍 useProfileData getActiveProfileId: Determining active profile...');
    
    // First check centralized function (handles admin impersonation)
    const centralizedId = getActiveProfileId();
    if (centralizedId) {
      console.log('🔍 - Using centralized profile ID:', centralizedId);
      return centralizedId;
    }
    
    // Then check scout view
    if (isScoutView && impersonatedProfileId) {
      console.log('🔍 - Using scout view profile ID:', impersonatedProfileId);
      return impersonatedProfileId;
    }
    
    // Then check parent context
    if (athleteProfileId) {
      console.log('🔍 - Using athleteProfileId from parent context:', athleteProfileId);
      return athleteProfileId;
    }
    
    // Then check parent impersonation
    if (isImpersonating && athleteId) {
      console.log('🔍 - Using impersonated athleteId:', athleteId);
      return athleteId;
    }
    
    // Finally use current profile
    const fallbackId = profile?.id || '';
    console.log('🔍 - Using fallback profile ID:', fallbackId);
    return fallbackId;
  }, [isScoutView, impersonatedProfileId, athleteProfileId, isImpersonating, athleteId, profile?.id]);

  const activeProfileId = getActiveProfileIdInternal();
  console.log('🔍 useProfileData: Final activeProfileId:', activeProfileId);

  // Get edit permissions to determine read-only mode correctly
  const { canEdit } = useProfileEditPermissions();
  
  // Check if we're in a read-only mode - use permissions instead of hardcoded logic
  const isReadOnlyMode = !canEdit;
  console.log('🔍 useProfileData: isReadOnlyMode:', isReadOnlyMode);

  // Fetch all profile data with proper joins for sports and positions
  const fetchProfileData = useCallback(async () => {
    if (!activeProfileId) {
      console.log('🔍 fetchProfileData: No activeProfileId, skipping fetch');
      return;
    }
    
    try {
      console.log("🔍 fetchProfileData: Fetching profile data for profile ID:", activeProfileId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          sports:sport_id (
            id,
            name,
            gender
          ),
          sport:sports!sport_id (
            id,
            name
          ),
          primary_position:positions!primary_position_id (
            id,
            position_name,
            sport_id
          ),
          secondary_position:positions!secondary_position_id (
            id,
            position_name,
            sport_id
          )
        `)
        .eq('id', activeProfileId)
        .single();
      
      if (error) {
        console.error('🔍 fetchProfileData: Error fetching profile data:', error);
        throw error;
      }
      
      console.log("🔍 fetchProfileData: Profile data fetched successfully:", data);
      setProfileData(data);
      
      // Set profile completion percentage if available
      if (data?.profile_completion) {
        setProfileCompletionPct(data.profile_completion);
      }
    } catch (error) {
      console.error('🔍 fetchProfileData: Error in try/catch:', error);
    }
  }, [activeProfileId]);

  // Fetch academic data
  const fetchAcademicData = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_academics')
        .select('*')
        .eq('profile_id', activeProfileId)
        .maybeSingle();
      
      if (error) throw error;
      setAcademicData(data);
    } catch (error) {
      console.error('Error fetching academic data:', error);
    }
  }, [activeProfileId]);

  // Fetch social media
  const fetchSocialMedia = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_social_media')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setSocialMedia(data || []);
    } catch (error) {
      console.error('Error fetching social media:', error);
    }
  }, [activeProfileId]);

  // Fetch press links
  const fetchPress = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_press')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setPress(data || []);
    } catch (error) {
      console.error('Error fetching press:', error);
    }
  }, [activeProfileId]);

  // Fetch experiences
  const fetchExperiences = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_experiences')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setExperiences(data || []);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    }
  }, [activeProfileId]);

  // Fetch events - Prioritize profile_events over calendar_events
  const fetchEvents = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Fetching events for profile:', activeProfileId);
      
      let data = null;
      let error = null;
      
      const result1 = await supabase
        .from('profile_events')
        .select('*')
        .eq('profile_id', activeProfileId)
        .order('event_date', { ascending: false });
      
      if (result1.data && result1.data.length > 0) {
        data = result1.data;
        console.log('🔍 Found events in profile_events (preferred):', data);
      } else if (result1.error && result1.error.code !== 'PGRST116') {
        const result2 = await supabase
          .from('calendar_events')
          .select('*')
          .eq('profile_id', activeProfileId)
          .order('start_date', { ascending: false });
        
        data = result2.data;
        error = result2.error;
        if (data && data.length > 0) {
          console.log('🔍 Found events in calendar_events (fallback):', data);
        }
      }
      
      console.log('🔍 Events fetched successfully:', data);
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [activeProfileId]);

  // Fetch preferences - Fix table name and query
  const fetchPreferences = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Fetching preferences for profile:', activeProfileId);
      
      let data = null;
      let error = null;
      
      const result1 = await supabase
        .from('profile_preferences')
        .select('*')
        .eq('profile_id', activeProfileId)
        .maybeSingle();
      
      if (result1.data) {
        data = result1.data;
        // Map major_interest to intended_major for frontend compatibility
        if (data.major_interest !== undefined) {
          data.intended_major = data.major_interest;
        }
        console.log('🔍 Found preferences in profile_preferences:', data);
      } else if (result1.error && result1.error.code !== 'PGRST116') {
        const result2 = await supabase
          .from('profile_recruiting_preferences')
          .select('*')
          .eq('profile_id', activeProfileId)
          .maybeSingle();
        
        data = result2.data;
        error = result2.error;
        if (data) {
          console.log('🔍 Found preferences in profile_recruiting_preferences:', data);
        }
      }
      
      console.log('🔍 Preferences fetched successfully:', data);
      setPreferences(data || null);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, [activeProfileId]);

  // Fetch offers - Fix table name and query
  const fetchOffers = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Fetching offers for profile:', activeProfileId);
      
      let data = null;
      let error = null;
      
      const result1 = await supabase
        .from('profile_offers')
        .select('*')
        .eq('profile_id', activeProfileId)
        .order('created_at', { ascending: false });
      
      if (result1.data && result1.data.length > 0) {
        data = result1.data;
        console.log('🔍 Found offers in profile_offers:', data);
      } else if (result1.error && result1.error.code !== 'PGRST116') {
        const result2 = await supabase
          .from('recruiting_offers')
          .select(`
            *,
            college:college_id(id, name, city, state, division)
          `)
          .eq('profile_id', activeProfileId)
          .order('created_at', { ascending: false });
        
        data = result2.data;
        error = result2.error;
        if (data && data.length > 0) {
          console.log('🔍 Found offers in recruiting_offers:', data);
        }
      }
      
      console.log('🔍 Offers fetched successfully:', data);
      setOffers(data || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  }, [activeProfileId]);

  // Fetch academic achievements
  const fetchAcademicAchievements = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_academic_achievements')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setAcademicAchievements(data || []);
    } catch (error) {
      console.error('Error fetching academic achievements:', error);
    }
  }, [activeProfileId]);

  // Fetch athletic achievements
  const fetchAthleticAchievements = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_athletic_achievements')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setAthleticAchievements(data || []);
    } catch (error) {
      console.error('Error fetching athletic achievements:', error);
    }
  }, [activeProfileId]);

  // Fetch sport stats
  const fetchSportStats = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_statistics')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setSportStats(data || []);
    } catch (error) {
      console.error('Error fetching sport stats:', error);
    }
  }, [activeProfileId]);

  // Fetch combine scores
  const fetchCombineScores = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('combine_scores')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setCombineScores(data || []);
    } catch (error) {
      console.error('Error fetching combine scores:', error);
    }
  }, [activeProfileId]);

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_feedback')
        .select('*')
        .eq('profile_id', activeProfileId)
        .eq('visible_to_athlete', true);
      
      if (error) throw error;
      setRecommendations(data || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  }, [activeProfileId]);

  // Fetch media items
  const fetchMediaItems = useCallback(async () => {
    if (!activeProfileId) return;
    
    try {
      const { data, error } = await supabase
        .from('profile_media')
        .select('*')
        .eq('profile_id', activeProfileId);
      
      if (error) throw error;
      setMediaItems(data || []);
    } catch (error) {
      console.error('Error fetching media items:', error);
    }
  }, [activeProfileId]);

  // Refresh all profile data
  const refreshAllProfileData = useCallback(async () => {
    if (!activeProfileId) {
      console.log('🔍 refreshAllProfileData: No activeProfileId, setting loading to false');
      setIsLoading(false);
      return;
    }

    console.log('🔍 refreshAllProfileData: Starting refresh for profile ID:', activeProfileId);
    setIsLoading(true);
    await Promise.all([
      fetchProfileData(),
      fetchAcademicData(),
      fetchSocialMedia(),
      fetchPress(),
      fetchExperiences(),
      fetchEvents(),
      fetchPreferences(),
      fetchOffers(),
      fetchAcademicAchievements(),
      fetchAthleticAchievements(),
      fetchSportStats(),
      fetchCombineScores(),
      fetchRecommendations(),
      fetchMediaItems(),
    ]);
    console.log('🔍 refreshAllProfileData: All data refreshed, setting loading to false');
    setIsLoading(false);
  }, [
    activeProfileId,
    fetchProfileData,
    fetchAcademicData,
    fetchSocialMedia,
    fetchPress,
    fetchExperiences,
    fetchEvents,
    fetchPreferences,
    fetchOffers,
    fetchAcademicAchievements,
    fetchAthleticAchievements,
    fetchSportStats,
    fetchCombineScores,
    fetchRecommendations,
    fetchMediaItems,
  ]);

  // Initial load - refresh when activeProfileId changes
  useEffect(() => {
    console.log('🔍 useProfileData useEffect: activeProfileId changed to:', activeProfileId);
    refreshAllProfileData();
  }, [refreshAllProfileData]);

  // CRUD operations - all include read-only mode check
  const updateAcademics = async (data: Academics): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }

    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id || user.id;
      
      // Helper function to convert string to number or null
      const parseNumericValue = (value: string | number | null | undefined): number | null => {
        if (value === null || value === undefined || value === '') {
          return null;
        }
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        }
        return value;
      };
      
      // Convert empty strings to null for numeric fields to prevent database errors
      const cleanedData = {
        ...data,
        profile_id: profileId,
        gpa: parseNumericValue(data.gpa),
        sat_total: parseNumericValue(data.sat_total),
        sat_reading_writing: parseNumericValue(data.sat_reading_writing),
        sat_math: parseNumericValue(data.sat_math),
        act_composite: parseNumericValue(data.act_composite),
        act_english: parseNumericValue(data.act_english),
        act_math: parseNumericValue(data.act_math),
        act_reading: parseNumericValue(data.act_reading),
        act_science: parseNumericValue(data.act_science)
      };
      
      console.log('Cleaned academic data:', cleanedData);
      
      if (academicData?.id) {
        const { error } = await supabase
          .from('profile_academics')
          .update(cleanedData)
          .eq('id', academicData.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('profile_academics')
          .insert(cleanedData);
          
        if (error) throw error;
      }
      
      fetchAcademicData();
      toast.success('Academic information updated successfully');
    } catch (error) {
      console.error('Error updating academic data:', error);
      toast.error('Failed to update academic information');
    }
  };

  const saveSocialMedia = async (socialMediaData: SocialMedia[]): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }

    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id || user.id;
      
      const { error: deleteError } = await supabase
        .from('profile_social_media')
        .delete()
        .eq('profile_id', profileId);
        
      if (deleteError) throw deleteError;
      
      if (socialMediaData.length > 0) {
        const dataToInsert = socialMediaData.map(item => ({
          ...item,
          profile_id: profileId
        }));
        
        const { error: insertError } = await supabase
          .from('profile_social_media')
          .insert(dataToInsert);
          
        if (insertError) throw insertError;
      }
      
      fetchSocialMedia();
      toast.success('Social media profiles updated successfully');
    } catch (error) {
      console.error('Error saving social media:', error);
      toast.error('Failed to update social media profiles');
    }
  };

  const addPress = async (pressData: Partial<PressEntry>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id;
      
      if (!profileId) {
        toast.error('No profile selected');
        return;
      }
      
      const dataToInsert = {
        ...pressData,
        profile_id: profileId,
      };
      
      const { error } = await supabase
        .from('profile_press')
        .insert(dataToInsert);
        
      if (error) throw error;
      
      fetchPress();
      toast.success('Press entry added successfully');
    } catch (error) {
      console.error('Error adding press entry:', error);
      toast.error('Failed to add press entry');
    }
  };

  const updatePress = async (pressData: PressEntry): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_press')
        .update(pressData)
        .eq('id', pressData.id);
        
      if (error) throw error;
      
      fetchPress();
      toast.success('Press entry updated successfully');
    } catch (error) {
      console.error('Error updating press entry:', error);
      toast.error('Failed to update press entry');
    }
  };

  const deletePress = async (pressId: string): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_press')
        .delete()
        .eq('id', pressId);
        
      if (error) throw error;
      
      fetchPress();
      toast.success('Press entry deleted successfully');
    } catch (error) {
      console.error('Error deleting press entry:', error);
      toast.error('Failed to delete press entry');
    }
  };

  const addExperience = async (experienceData: Partial<ProfileExperience>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id || user.id;
      // Clean the data to match database schema
      const dataToInsert = {
        profile_id: profileId,
        experience_type: experienceData.experience_type || null,
        organization_name: experienceData.organization_name || null,
        team_name: experienceData.team_name || null,
        location: experienceData.location || null,
        start_date: experienceData.start_date || null,
        end_date: experienceData.end_date || null,
        description: experienceData.description || null,
        coach_name: experienceData.coach_name || null,
        coach_email: experienceData.coach_email || null,
        coach_phone: experienceData.coach_phone || null,
      };
      
      console.log('🔍 addExperience: Input data:', experienceData);
      console.log('🔍 addExperience: Profile ID:', profileId);
      console.log('🔍 addExperience: Data to insert:', dataToInsert);
      
      const { error } = await supabase
        .from('profile_experiences')
        .insert(dataToInsert);
        
      if (error) {
        console.error('🔍 addExperience: Supabase error details:', error);
        throw error;
      }
      
      fetchExperiences();
      toast.success('Experience added successfully');
    } catch (error) {
      console.error('Error adding experience:', error);
      toast.error('Failed to add experience');
    }
  };

  const updateExperience = async (experienceData: ProfileExperience): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_experiences')
        .update(experienceData)
        .eq('id', experienceData.id);
        
      if (error) throw error;
      
      fetchExperiences();
      toast.success('Experience updated successfully');
    } catch (error) {
      console.error('Error updating experience:', error);
      toast.error('Failed to update experience');
    }
  };

  const deleteExperience = async (experienceId: string): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_experiences')
        .delete()
        .eq('id', experienceId);
        
      if (error) throw error;
      
      fetchExperiences();
      toast.success('Experience deleted successfully');
    } catch (error) {
      console.error('Error deleting experience:', error);
      toast.error('Failed to delete experience');
    }
  };

  const addEvent = async (eventData: Partial<Event>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id || user.id;
      
      // Format date properly to avoid timezone issues
      let formattedDate = eventData.event_date;
      if (eventData.event_date instanceof Date) {
        formattedDate = eventData.event_date.toISOString().split('T')[0];
      }
      
      const dataToInsert = {
        ...eventData,
        event_date: formattedDate,
        profile_id: profileId,
      };
      
      const { error } = await supabase
        .from('profile_events')
        .insert(dataToInsert);
        
      if (error) throw error;
      
      fetchEvents();
      toast.success('Event added successfully');
    } catch (error) {
      console.error('Error adding event:', error);
      toast.error('Failed to add event');
    }
  };

  const updateEvent = async (eventData: Partial<Event> & { id: string }): Promise<void> => {
    const { id: eventId, ...dataToUpdate } = eventData;
    console.log('🚀 updateEvent called:', { eventId, dataToUpdate, isReadOnlyMode, userId: user?.id, athleteProfileId, impersonatedProfileId });
    
    if (isReadOnlyMode) {
      console.error('❌ Blocked: Read-only mode');
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) {
      console.error('❌ Blocked: No user/athlete/impersonated profile ID');
      return;
    }
    
    try {
      // Format date properly to avoid timezone issues
      let formattedData = { ...dataToUpdate };
      if (dataToUpdate.event_date instanceof Date) {
        formattedData.event_date = dataToUpdate.event_date.toISOString().split('T')[0];
      }
      
      console.log('📤 Sending update to database:', { eventId, formattedData });
      
      const { data, error, count } = await supabase
        .from('profile_events')
        .update(formattedData)
        .eq('id', eventId)
        .select();
        
      console.log('📥 Update result:', { data, error, count, eventId, formattedData });
        
      if (error) throw error;
      
      if (!data || data.length === 0) {
        console.error('❌ RLS Policy blocked update - no rows affected');
        throw new Error('Update failed - permission denied or event not found');
      }
      
      // Force immediate re-fetch of events with a slight delay to ensure DB has processed
      await new Promise(resolve => setTimeout(resolve, 100));
      await fetchEvents();
      console.log('✅ Events refreshed after update');
      toast.success('Event updated successfully');
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const deleteEvent = async (eventId: string): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_events')
        .delete()
        .eq('id', eventId);
        
      if (error) throw error;
      
      fetchEvents();
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const updatePreferences = async (data: Partial<PreferenceData>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }

    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Updating preferences for profile:', activeProfileId, 'with data:', data);
      
      // Map intended_major to major_interest for database compatibility
      const mappedData = { ...data };
      if (mappedData.intended_major !== undefined) {
        mappedData.major_interest = mappedData.intended_major;
        delete mappedData.intended_major;
      }
      
      const dataToSave = { ...mappedData, profile_id: activeProfileId };
      
      let tableName = 'profile_preferences';
      if (!preferences?.id) {
        const checkResult = await supabase
          .from('profile_recruiting_preferences')
          .select('id')
          .eq('profile_id', activeProfileId)
          .maybeSingle();
        
        if (checkResult.data) {
          tableName = 'profile_recruiting_preferences';
        }
      }
      
      if (preferences?.id) {
        const { error } = await supabase
          .from(tableName)
          .update(dataToSave)
          .eq('id', preferences.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from(tableName)
          .insert(dataToSave);
          
        if (error) throw error;
      }
      
      await fetchPreferences();
      toast.success('Preferences updated successfully');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    }
  };

  const addOffer = async (offerData: Partial<ProfileOffer>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Adding offer for profile:', activeProfileId);
      const dataToInsert = {
        ...offerData,
        profile_id: activeProfileId,
      };
      
      let error = null;
      const result1 = await supabase
        .from('profile_offers')
        .insert(dataToInsert);
      
      if (result1.error) {
        const result2 = await supabase
          .from('recruiting_offers')
          .insert(dataToInsert);
        error = result2.error;
      }
      
      if (error) throw error;
      
      await fetchOffers();
      toast.success('Offer added successfully');
    } catch (error) {
      console.error('Error adding offer:', error);
      toast.error('Failed to add offer');
    }
  };

  const updateOffer = async (offerData: ProfileOffer): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Updating offer for profile:', activeProfileId);
      const { error } = await supabase
        .from('profile_offers')
        .update(offerData)
        .eq('id', offerData.id);
        
      if (error) throw error;
      
      await fetchOffers();
      toast.success('Offer updated successfully');
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error('Failed to update offer');
    }
  };

  const deleteOffer = async (offerId: string): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!activeProfileId) return;
    
    try {
      console.log('🔍 Deleting offer for profile:', activeProfileId);
      const { error } = await supabase
        .from('profile_offers')
        .delete()
        .eq('id', offerId);
        
      if (error) throw error;
      
      await fetchOffers();
      toast.success('Offer deleted successfully');
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const addAcademicAchievement = async (achievementData: Partial<AcademicAchievement>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const profileId = athleteProfileId || impersonatedProfileId || profile?.id || user.id;
      
      // Map the data to match database column names
      const dataToInsert = {
        profile_id: profileId,
        title: achievementData.title,
        description: achievementData.description || null,
        date_awarded: achievementData.date_awarded || null,
      };
      
      const { error } = await supabase
        .from('profile_academic_achievements')
        .insert(dataToInsert);
        
      if (error) throw error;
      
      fetchAcademicAchievements();
      toast.success('Academic achievement added successfully');
    } catch (error) {
      console.error('Error adding academic achievement:', error);
      toast.error('Failed to add academic achievement');
    }
  };

  const updateAcademicAchievement = async (achievementId: string, achievementData: Partial<AcademicAchievement>): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_academic_achievements')
        .update(achievementData)
        .eq('id', achievementId);
        
      if (error) throw error;
      
      fetchAcademicAchievements();
      toast.success('Academic achievement updated successfully');
    } catch (error) {
      console.error('Error updating academic achievement:', error);
      toast.error('Failed to update academic achievement');
    }
  };

  const deleteAcademicAchievement = async (achievementId: string): Promise<void> => {
    if (isReadOnlyMode) {
      toast.error('Cannot modify data while viewing athlete profile');
      return;
    }
    
    if (!user?.id && !athleteProfileId && !impersonatedProfileId) return;
    
    try {
      const { error } = await supabase
        .from('profile_academic_achievements')
        .delete()
        .eq('id', achievementId);
        
      if (error) throw error;
      
      fetchAcademicAchievements();
      toast.success('Academic achievement deleted successfully');
    } catch (error) {
      console.error('Error deleting academic achievement:', error);
      toast.error('Failed to delete academic achievement');
    }
  };

  return {
    profileData,
    isLoading,
    isUpdating,
    academicData,
    updateAcademics,
    socialMedia,
    saveSocialMedia,
    press,
    addPress,
    updatePress,
    deletePress,
    experiences,
    addExperience,
    updateExperience,
    deleteExperience,
    events,
    addEvent,
    updateEvent,
    deleteEvent,
    preferences,
    updatePreferences,
    offers,
    addOffer,
    updateOffer,
    deleteOffer,
    academicAchievements,
    addAcademicAchievement,
    updateAcademicAchievement,
    deleteAcademicAchievement,
    athleticAchievements,
    sportStats,
    combineScores,
    recommendations,
    mediaItems,
    profileCompletionPct,
    refreshAllProfileData,
  };
}

export type { Academics, AcademicAchievement };
